import {
  AloftArea,
  AloftPrice,
  Improvements,
  expected,
  isDefined,
  isUndefined,
  toTitleCase,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  DetailFeatures,
  FEATURES,
  Feature,
  getAdditionalStructuresQualityLabel,
} from '../../../constants.ts';
import { getFeatureQualityLabel } from '../../../utils/getFeatureQualityLabel.ts';
import { selectAdjustments } from '../adjustmentSelectors/selectAdjustments.ts';
import { selectAppliedAdditionStructureAdjsDisplay } from '../adjustmentSelectors/selectAppliedAdditionalStructureAdjsDisplay.ts';
import {
  CostNewByCategory,
  selectCostNewByCategories,
} from '../costSelectors/selectCostNewByCategory.ts';
import {
  selectAdjustmentSettings,
  selectCompSequence,
  selectDepreciateSeparately,
} from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import {
  selectDepreciationRatiosByCategory,
  selectDepreciationRatiosByCategoryDisplay,
} from '../selectDepreciationRatioByCategory.ts';
import { selectOrderFeatures } from '../selectOrderFeatures.ts';
import { getCommentaryTableHelperText } from '../utils/getCommentaryTableHelperText.ts';

type StandardKey = Readonly<
  Extract<Feature, 'shed' | 'workshop' | 'barn' | 'adu' | 'fireplace' | 'manualCost' | 'attic'>
>;

type SubKey = 'garage' | 'carport' | 'porch' | 'patio' | 'deck' | 'pool' | 'spa';

const _customAdditionalStructureKeys = ['garageCarport', 'porchPatioDeck', 'poolSpa'] as const;
type MultiKey = (typeof _customAdditionalStructureKeys)[number];

export type AdditionalStructureKey = StandardKey | MultiKey;

const isCustomFeature = (feature: MultiFeature | StandardFeature): feature is MultiFeature =>
  'subKeys' in feature;

type StandardFeature = {
  key: StandardKey;
  label: string;
  getInputColValues: (improvement: Improvements) => Array<string | string[]>;
  getInputColHeaders: () => string[];
  hideFeatureCol?: boolean;
  shouldRender: (orderFeatures: DetailFeatures[]) => boolean;
  hasFeature: (improvement: Improvements) => boolean;
  getFeatureColValue: (improvement: Improvements) => string;
};

type MultiFeature = {
  key: MultiKey;
  subKeys: Readonly<SubKey[]>;
  label: string;
  getInputColValues: (improvement: Improvements) => Array<string | string[]>;
  getInputColHeaders: () => string[];
  hideFeatureCol?: boolean;
  shouldRender: (orderFeatures: DetailFeatures[]) => boolean;
  hasFeature: (improvement: Improvements) => boolean;
  getFeatureColValue: (improvement: Improvements) => string[] | string;
};

const ADDITIONAL_STRUCTURES_FEATURES: Readonly<Array<MultiFeature | StandardFeature>> = [
  {
    key: 'attic',
    label: 'Attic',
    getInputColValues: ({ attic }: Improvements) => [
      isDefined(attic) && isDefined(attic.area)
        ? AloftArea.schema.parse(attic.area).toString()
        : '-',
    ],
    getInputColHeaders: () => ['Area'],
    shouldRender: (orderFeatures: DetailFeatures[]) => orderFeatures.includes('attic'),
    hasFeature: FEATURES.attic.hasFeature,
    getFeatureColValue: (improvement: Improvements) =>
      FEATURES.attic.hasFeature(improvement) ? 'Attic' : 'None',
  },
  {
    key: 'garageCarport',
    subKeys: ['garage', 'carport'],
    label: 'Garage/Carport',
    getInputColHeaders: () => ['Quality', 'Area'],
    getInputColValues: ({ garage, carport, craftsmanQuality }: Improvements) => {
      const quality = [];
      const area = [];

      if (isDefined(garage) && garage.hasFeature && isDefined(garage.area)) {
        area.push(AloftArea.schema.parse(garage.area).toString());
        quality.push(
          isDefined(garage?.quality)
            ? getAdditionalStructuresQualityLabel(garage?.quality)
            : getAdditionalStructuresQualityLabel(craftsmanQuality),
        );
      }
      if (isDefined(carport) && carport.hasFeature && isDefined(carport.area)) {
        area.push(AloftArea.schema.parse(carport.area).toString());
        quality.push(
          isDefined(carport?.quality)
            ? getAdditionalStructuresQualityLabel(carport?.quality)
            : getAdditionalStructuresQualityLabel(craftsmanQuality),
        );
      }

      if ((!isDefined(garage) || !garage.hasFeature || !isDefined(garage.area)) &&
          (!isDefined(carport) || !carport.hasFeature || !isDefined(carport.area))) {
        area.push('-');
        quality.push('-');
      }

      return [quality, area];
    },
    shouldRender: (orderFeatures: DetailFeatures[]) =>
      orderFeatures.includes('garage') || orderFeatures.includes('carport'),
    hasFeature: (improvement: Improvements) =>
      FEATURES.garage.hasFeature(improvement) || FEATURES.carport.hasFeature(improvement),
    getFeatureColValue: (improvement: Improvements) => {
      const rows = [
        ...(FEATURES.garage.hasFeature(improvement) ? ['Garage'] : []),
        ...(FEATURES.carport.hasFeature(improvement) ? ['Carport'] : []),
      ];

      return rows.length > 0 ? rows : 'None';
    },
  },
  {
    key: 'porchPatioDeck',
    subKeys: ['porch', 'patio', 'deck'],
    label: 'Porch/Patio/Deck',
    getInputColHeaders: () => ['Quality', 'Area'],
    getInputColValues: ({ porch, patio, deck, craftsmanQuality }: Improvements) => {
      const quality = [];
      const area = [];

      if (isDefined(porch) && porch.hasFeature && isDefined(porch.area)) {
        area.push(AloftArea.schema.parse(porch.area).toString());
        quality.push(
          isDefined(porch?.quality)
            ? getAdditionalStructuresQualityLabel(porch?.quality)
            : getAdditionalStructuresQualityLabel(craftsmanQuality),
        );
      }
      if (isDefined(patio) && patio.hasFeature && isDefined(patio.area)) {
        area.push(AloftArea.schema.parse(patio.area).toString());
        quality.push(
          isDefined(patio?.quality)
            ? getAdditionalStructuresQualityLabel(patio?.quality)
            : getAdditionalStructuresQualityLabel(craftsmanQuality),
        );
      }
      if (isDefined(deck) && deck.hasFeature && isDefined(deck.area)) {
        area.push(AloftArea.schema.parse(deck.area).toString());
        quality.push(
          isDefined(deck?.quality)
            ? getAdditionalStructuresQualityLabel(deck?.quality)
            : getAdditionalStructuresQualityLabel(craftsmanQuality),
        );
      }

      if ((!isDefined(porch) || !porch.hasFeature || !isDefined(porch.area)) &&
          (!isDefined(patio) || !patio.hasFeature || !isDefined(patio.area)) &&
          (!isDefined(deck) || !deck.hasFeature || !isDefined(deck.area))) {
        area.push('-');
        quality.push('-');
      }

      return [quality, area];
    },
    shouldRender: (orderFeatures: DetailFeatures[]) =>
      orderFeatures.includes('porch') ||
      orderFeatures.includes('patio') ||
      orderFeatures.includes('deck'),
    hasFeature: (improvement: Improvements) =>
      FEATURES.porch.hasFeature(improvement) ||
      FEATURES.patio.hasFeature(improvement) ||
      FEATURES.deck.hasFeature(improvement),
    getFeatureColValue: (improvement: Improvements) => {
      const rows = [
        ...(FEATURES.porch.hasFeature(improvement) ? ['Porch'] : []),
        ...(FEATURES.patio.hasFeature(improvement) ? ['Patio'] : []),
        ...(FEATURES.deck.hasFeature(improvement) ? ['Deck'] : []),
      ];

      return rows.length > 0 ? rows : 'None';
    },
  },
  {
    key: 'poolSpa',
    label: 'Pool/Spa',
    subKeys: ['pool', 'spa'],
    getInputColHeaders: () => ['Quality', 'Area'],
    getInputColValues: ({ pool, spa }: Improvements) => {
      const quality = [];
      const area = [];

      if (isDefined(pool) && pool.hasFeature && isDefined(pool.area)) {
        area.push(AloftArea.schema.parse(pool.area).toString());
        quality.push(getFeatureQualityLabel('pool', pool.quality));
      }
      if (isDefined(spa) && spa.hasFeature && isDefined(spa.area)) {
        area.push(AloftArea.schema.parse(spa.area).toString());
        quality.push(getFeatureQualityLabel('spa', spa.quality));
      }

      if ((!isDefined(pool) || !pool.hasFeature || !isDefined(pool.area)) && (!isDefined(spa) || !spa.hasFeature || !isDefined(spa.area))) {
        area.push('-');
        quality.push('-');
      }

      return [quality, area];
    },
    shouldRender: (orderFeatures: DetailFeatures[]) =>
      orderFeatures.includes('pool') || orderFeatures.includes('spa'),
    hasFeature: (improvement: Improvements) =>
      FEATURES.pool.hasFeature(improvement) || FEATURES.spa.hasFeature(improvement),
    getFeatureColValue: (improvement: Improvements) => {
      const rows = [
        ...(FEATURES.pool.hasFeature(improvement) ? ['Pool'] : []),
        ...(FEATURES.spa.hasFeature(improvement) ? ['Spa'] : []),
      ];

      return rows.length > 0 ? rows : 'None';
    },
  },
  {
    key: 'shed',
    label: 'Shed',
    getInputColHeaders: () => ['Quality', 'Area'],
    getInputColValues: ({ shed }: Improvements) => [
      shed?.quality ? getFeatureQualityLabel('shed', shed.quality) : '-',
      isDefined(shed) && isDefined(shed.area) ? AloftArea.schema.parse(shed.area).toString() : '-',
    ],
    shouldRender: (orderFeatures: DetailFeatures[]) => orderFeatures.includes('shed'),
    hasFeature: FEATURES.shed.hasFeature,
    getFeatureColValue: (improvement: Improvements) =>
      FEATURES.shed.hasFeature(improvement) ? 'Shed' : 'None',
  },
  {
    key: 'workshop',
    label: 'Workshop',
    getInputColHeaders: () => ['Quality', 'Area'],
    getInputColValues: ({ workshop }: Improvements) => [
      workshop?.quality ? getFeatureQualityLabel('shed', workshop.quality) : '-',
      isDefined(workshop) && isDefined(workshop.area)
        ? AloftArea.schema.parse(workshop.area).toString()
        : '-',
    ],
    shouldRender: (orderFeatures: DetailFeatures[]) => orderFeatures.includes('workshop'),
    hasFeature: FEATURES.workshop.hasFeature,
    getFeatureColValue: (improvement: Improvements) =>
      FEATURES.workshop.hasFeature(improvement) ? 'Workshop' : 'None',
  },
  {
    key: 'barn',
    label: 'Barn',
    getInputColHeaders: () => ['Quality', 'Area'],
    getInputColValues: ({ barn }: Improvements) => [
      barn?.quality ? getFeatureQualityLabel('shed', barn.quality) : '-',
      isDefined(barn) && isDefined(barn.area) ? AloftArea.schema.parse(barn.area).toString() : '-',
    ],
    shouldRender: (orderFeatures: DetailFeatures[]) => orderFeatures.includes('barn'),
    hasFeature: FEATURES.barn.hasFeature,
    getFeatureColValue: (improvement: Improvements) =>
      FEATURES.barn.hasFeature(improvement) ? 'Barn' : 'None',
  },
  {
    key: 'adu',
    label: 'ADU',
    getInputColHeaders: () => ['Quality', 'Area'],
    getInputColValues: ({ adu }: Improvements) => [
      adu?.quality?.toString() ?? '-',
      isDefined(adu) && isDefined(adu.area) ? AloftArea.schema.parse(adu.area).toString() : '-',
    ],
    shouldRender: (orderFeatures: DetailFeatures[]) => orderFeatures.includes('adu'),
    hasFeature: FEATURES.adu.hasFeature,
    getFeatureColValue: (improvement: Improvements) =>
      FEATURES.adu.hasFeature(improvement) ? 'ADU' : 'None',
  },
  {
    key: 'fireplace',
    label: 'Fireplace',
    getInputColValues: ({ fireplace }: Improvements) => [
      fireplace?.type ? toTitleCase(fireplace.type.toString()) : '-',
      fireplace?.count?.toString() ?? '-',
    ],
    getInputColHeaders: () => ['Type', 'Count'],
    shouldRender: (orderFeatures: DetailFeatures[]) => orderFeatures.includes('fireplace'),
    hasFeature: FEATURES.fireplace.hasFeature,
    getFeatureColValue: (improvement: Improvements) =>
      FEATURES.fireplace.hasFeature(improvement) ? 'Fireplace' : 'None',
  },
  {
    key: 'manualCost',
    label: 'Manual Cost',
    getInputColHeaders: () => [],
    getInputColValues: () => [],
    hideFeatureCol: true,
    shouldRender: (orderFeatures: DetailFeatures[]) => orderFeatures.includes('manualCost'),
    hasFeature: FEATURES.manualCost.hasFeature,
    getFeatureColValue: (improvement: Improvements) =>
      improvement.manualCostDisplayName || FEATURES.manualCost.hasFeature(improvement)
        ? 'Manual Cost'
        : 'None',
  },
] as const;

export const additionalStructureFeatureKeysToRender = createSelector(
  [selectOrderFeatures],
  (orderFeatures) => {
    const featuresToRender = ADDITIONAL_STRUCTURES_FEATURES.filter((feature) =>
      feature.shouldRender(orderFeatures),
    );

    return featuresToRender.map(({ key }) => key);
  },
);

const selectAdditionalStructureCommentaryData = createSelector(
  [
    selectCompSequence,
    selectImprovementsEntities,
    selectCostNewByCategories,
    selectDepreciationRatiosByCategoryDisplay,
    selectDepreciationRatiosByCategory,
    selectAdjustments,
    selectOrderFeatures,
    selectAdjustmentSettings,
    selectAppliedAdditionStructureAdjsDisplay,
    selectDepreciateSeparately,
    selectManualAdjustmentsEntities,
  ],
  (
    compSequence,
    improvements,
    costNewsDisplay,
    depreciationsDisplay,
    depreciationRatios,
    adjustments,
    orderFeatures,
    adjustmentSettings,
    appliedAdjustmentsDisplay,
    depreciateSeparately,
    manualAdjustmentsEntities,
  ) => {
    const featuresToRender = ADDITIONAL_STRUCTURES_FEATURES.filter((feature) =>
      feature.shouldRender(orderFeatures),
    );

    return featuresToRender.map((feature) => ({
      key: feature.key,
      label:
        (feature.key === 'manualCost' && improvements.subject?.manualCostDisplayName) ||
        feature.label,
      helperText:
        // TODO: Add spa and manual cost to rounding/thresholds?
        feature.key === 'manualCost' || feature.key === 'poolSpa'
          ? undefined
          : getCommentaryTableHelperText(
              adjustmentSettings,
              feature.key,
              undefined,
              manualAdjustmentsEntities,
            ),
      inputColHeaders: feature.getInputColHeaders(),
      hideFeatureCol: feature.hideFeatureCol,
      rowData: ['subject', ...compSequence].map((id, index) => {
        const improvement = improvements[id];

        const combinedCostNew = isCustomFeature(feature)
          ? [
              feature.subKeys
                .map((key) => costNewsDisplay[id]?.[key])
                .reduce((a, b) => a?.add(b) ?? b, undefined),
            ]
          : [costNewsDisplay[id]?.[feature.key]];

        const separateCostNew = isCustomFeature(feature)
          ? feature.subKeys
              .filter((key) => isDefined(improvement?.[key]))
              .map((key) => costNewsDisplay[id]?.[key])
          : [costNewsDisplay[id]?.[feature.key]];

        const costNew = depreciateSeparately ? separateCostNew : combinedCostNew;

        // this works because the Multifeature groups are the same
        // as the groups we use for separate depreciation (see
        // DEPRECIATION_SEGMENTS). if that changes, we'll need to do
        // something different to get the depreciation for each
        // feature.
        const depreciationKey =
          isCustomFeature(feature) && feature.subKeys.length >= 1 && isDefined(feature.subKeys[0])
            ? feature.subKeys[0]
            : (feature.key as keyof CostNewByCategory);

        // const depreciationRatio = depreciationRatios?.[id]?.[depreciationKey];

        const combinedDepreciationDisplay = [depreciationsDisplay?.[id]?.[depreciationKey] ?? '-'];

        const separateDepreciationDisplay = isCustomFeature(feature)
          ? feature.subKeys
              .filter((key) => isDefined(improvement?.[key]))
              .map((key) => depreciationsDisplay?.[id]?.[key] ?? '-')
          : [depreciationsDisplay?.[id]?.[feature.key] ?? '-'];

        const depreciationDisplay = depreciateSeparately
          ? separateDepreciationDisplay
          : combinedDepreciationDisplay;

        const depreciationRatio = isCustomFeature(feature)
          ? feature.subKeys
              .filter((key) => isDefined(improvement?.[key]))
              .map((key) => depreciationRatios?.[id]?.[key])
          : [depreciationRatios?.[id]?.[feature.key]];

        if (isUndefined(improvement)) {
          throw new Error('Improvement not found');
        }

        return {
          label: id === 'subject' ? 'SUB' : `CP ${index}`,
          ...(!feature.hideFeatureCol && {
            feature: feature.getFeatureColValue(improvement),
          }),
          inputColValues: feature.getInputColValues(improvement),
          costNew: costNew?.map((cost) => cost?.toString(true) ?? '-') ?? '-',
          depreciation: depreciationDisplay ?? '-',
          depreciatedCost:
            costNew
              ?.reduce(
                (a, cost, idx) =>
                  isDefined(cost) && isDefined(depreciationRatio[idx])
                    ? a?.add(cost.sub(cost.mul(expected(depreciationRatio[idx]))))
                    : a,
                AloftPrice.zero,
              )
              ?.toString(true) ?? '-',
          adjustment: isCustomFeature(feature)
            ? (feature.subKeys
                .map((key) => adjustments?.[id]?.[key])
                .reduce((a, b) => a?.add(b) ?? b, undefined)
                ?.toString(true) ?? '-')
            : (adjustments?.[id]?.[feature.key]?.toString(true) ?? '-'),
          appliedAdjustment: appliedAdjustmentsDisplay?.[feature.key]?.[id]?.value ?? '-',
        };
      }),
    }));
  },
);

export const selectAdditionalStructureCommentaryTableDisplay = createSelector(
  [selectAdditionalStructureCommentaryData],
  (
    data,
  ): {
    key: AdditionalStructureKey;
    label: string;
    helperText?: string;
    tableData: [string[], ...Array<string | string[]>[]];
  }[] =>
    data.map((featureData) => ({
      key: featureData.key,
      label: featureData.label,
      helperText: featureData.helperText,
      tableData: [
        [
          '',
          ...(featureData.hideFeatureCol ? [] : ['Feature']),
          ...featureData.inputColHeaders,
          'Cost New',
          'Depreciation',
          'Dep. Cost',
          'Difference',
          'Adjustment',
        ],
        ...featureData.rowData.map((row) => [
          row.label,
          ...(row.feature ? [row.feature] : []),
          ...row.inputColValues,
          row.costNew,
          row.depreciation,
          row.depreciatedCost,
          row.adjustment,
          row.appliedAdjustment,
        ]),
      ],
    })),
);

export const selectAdditionalStructureCommentaryTableDisplayDictionary = createSelector(
  [selectAdditionalStructureCommentaryTableDisplay],
  (dataArray) => Object.fromEntries(dataArray.map((structure) => [structure.key, structure])),
);
